import React, { FCWithChildren, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { FAV_CONSTANTS } from '@constants/favorites';
import {
  convertAppliedHotFiltersToPayload,
  isAllowedHotFilterInUrlParams,
} from '@helpers/utils/hot-filters/hot-filters-parsing-util';
import { updateParamsInUrl } from '@helpers/routing';
import { scrollToTop } from '@helpers/utils/general';
import { FavoriteProduct } from '@interfaces/models/favoriteProduct';
import { PageType } from '@interfaces/common/common';
import { useHotFiltersContext } from '@context/hot-filters.context';
import useUser from '@hooks/user/use-user';
import useHotFiltersData from '@hooks/hotfilters/use-hot-filters-data';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import useProductFeedImpressionTracking from '@hooks/product-feed/use-product-feed-impression-tracking';
import useOnReveal from '@hooks/use-on-reveal';
import useFavoritesHotFiltersProductsQuery from '@hooks/favorites/v2/use-favorites-hot-filters-products-query';
import useFavouritesCMSQuery from '@hooks/favorites/use-favorites-cms-products-query';
import { useFavoritesEmptyState } from '@hooks/favorites/use-favorites-empty-state';
import { useFocusRestoreToChosenElement } from '@hooks/a11y/use-focus-restore';
import ProductFeedLayout from '@components/generic-product-feed/product-feed-layout/product-feed-layout';
import ProductFeed from '@components/generic-product-feed/product-feed/product-feed';
import FavoritesHeader from '@components/organisms/favorites/v2/favorites-header/favorites-header';
import HotFilters from '@components/organisms/hot-filters/hot-filters';
import FavoritesPagination from '@components/organisms/favorites/favorites-pagination/favorites-pagination';
import ProductsProviderListComponent from '@components/organisms/product-general-component/product-general-component';
import ToastMessage from '@components/atoms/toast-message/toast-message';
import { getValidFavSortOrder } from '@helpers/utils/profile-fav-sort/get-valid-sort-order';
import { FAV_DEFAULT_SORT_ORDER, FavSortByOptions } from '@constants/profile-fav-sort';

import styles from './favorites-feed-hot-filters.module.scss';

type FavoritesFeedHotFiltersProps = {
  pageType?: PageType;
  hasHeader: boolean;
  hasOnlySubheader?: boolean;
  customHeaderClass?: string;
  sortOrder?: FavSortByOptions;
};

const ImpressionTrackingWrapper = ({ children, onRowReveal, index, productsOnRow }) => {
  const { containerRef } = useOnReveal(() => onRowReveal(index, productsOnRow), 1, false);
  return (
    <div
      className="impressionTrackingContainer"
      ref={containerRef}
    >
      {children}
    </div>
  );
};

const FavoritesFeedHotFilters: React.FC<FavoritesFeedHotFiltersProps> = (props) => {
  const { pageType, sortOrder = FAV_DEFAULT_SORT_ORDER, hasHeader, hasOnlySubheader, customHeaderClass } = props;
  const { sendAnalyticEvent, updateDataLayer } = useAnalyticEvents('favorites');
  const { t } = useTranslation();
  const { userId } = useUser();
  const router = useRouter();
  const {
    sortBy,
    favoritesUserSlug,
    profileSlug,
    enableFavoritesHotFilter,
    enableFavoritesNewEmptyScreen,
    pageSize,
    ...filters
  } = router?.query as {
    sortBy: string;
    favoritesUserSlug?: string;
    profileSlug?: string;
    enableFavoritesHotFilter?: string;
    enableFavoritesNewEmptyScreen?: string;
    pageSize?: string;
  };

  const [sortByParam, setSortByParam] = useState<FavSortByOptions>(getValidFavSortOrder(sortOrder));
  const [itemsPerPage, setItemsPerPage] = useState(parseInt(pageSize) || FAV_CONSTANTS.FAV_DEFAULT_LIMIT);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>(filters || {});
  const [products, setProducts] = useState<FavoriteProduct[] | null>(null);
  const [cmsProductsState, setCmsProductsState] = useState<{ isLoaded: boolean; products?: FavoriteProduct[] | null }>({
    isLoaded: false,
    products: null,
  });
  const [shouldDisplayCMSBlockInEmptyState, setShouldDisplayCMSBlockInEmptyState] = useState<boolean>(false);
  const isHotFiltersEnabledAndInUrl = isAllowedHotFilterInUrlParams(appliedFilters);

  const {
    isLoadingProducts,
    isLoadingMoreProducts,
    handleApplyFilters,
    setOpenedHotFilterKey,
    isError,
    handleOpenFilter,
  } = useHotFiltersData({
    pageType,
    queryHook: useFavoritesHotFiltersProductsQuery,
    queryHookParams: {},
  });
  const {
    filteredItems,
    appliedHotFilters,
    setAppliedHotFilters,
    totalCount,
    pagination: paginationMeta,
    paginationQuery,
    setPaginationQuery,
    isUserSlug,
  } = useHotFiltersContext();

  const triggerFocusOnNextUpdate = useFocusRestoreToChosenElement(true, products, 'product_id_');

  const shouldDisplayHeader = totalCount > 0 || !shouldDisplayCMSBlockInEmptyState;

  const eventCategory = isUserSlug ? 'my_favourites' : 'member_favourites';
  const { onRowReveal, rowCount, hasWindowSizeBeenSet } = useProductFeedImpressionTracking(eventCategory);
  // `null` represents the state when data is fetching (initial state and while awaiting an API response) & hasn't yet set the data locally.
  const isFavoritesPage = pageType === 'favourite' || pageType === 'profile-favorites';
  const isFavoritesDataFetching = isLoadingProducts || (isFavoritesPage && products === null);
  const shouldShowHotFilters = isHotFiltersEnabledAndInUrl || totalCount >= 5;

  useEffect(() => {
    if (totalCount !== null || isHotFiltersEnabledAndInUrl) {
      updateDataLayer({
        sorting: sortBy,
        nb_items: totalCount?.toString() || '0',
        filters_details: JSON.stringify(convertAppliedHotFiltersToPayload(appliedFilters)),
        filters_applied: '{}',
        list_products: filteredItems,
      });
    }
  }, [totalCount, appliedFilters, sortBy]);

  useEffect(() => {
    setAppliedHotFilters(appliedFilters);
  }, [appliedFilters]);

  useEffect(() => {
    if (filteredItems) {
      const updatedProducts = filteredItems?.map((product: FavoriteProduct) => ({
        ...product,
        id: product.productId,
      }));
      setProducts(updatedProducts);
    }
  }, [filteredItems]);

  useEffect(() => {
    setSortByParam(getValidFavSortOrder(sortBy || sortOrder));
  }, [sortOrder, sortBy]);

  useEffect(() => {
    /*
     * For triggering the CMS query:
     * - Favorites Empty Screen feature flag must be enabled.
     * - Total favorite products count should be 0.
     * - The user must be logged in, and the user ID should match the favorites user ID. This applies to "My Favorites" only.
     * - Profile favorites will continue to use the existing state.
     * - URL should not contain any filters.
     */
    const shouldTriggerCMSQuery =
      totalCount === 0 && isUserSlug && !isHotFiltersEnabledAndInUrl && pageType === 'favourite';

    setShouldDisplayCMSBlockInEmptyState(shouldTriggerCMSQuery || false);
  }, [totalCount, isUserSlug, isHotFiltersEnabledAndInUrl, pageType]);

  const { favoritePageData } = useFavouritesCMSQuery({
    userId,
    shouldFetchCMSQuery: shouldDisplayCMSBlockInEmptyState && pageType === 'favourite',
  });

  const favouritesEmptyState = useFavoritesEmptyState({
    isHotFiltersEnabledAndInUrl,
    favoritePageData,
    eventCategory,
    pageType,
    shouldDisplayCMSBlockInEmptyState: shouldDisplayCMSBlockInEmptyState && cmsProductsState.isLoaded,
  });

  const handleApplyFiltersWithSort = (sortOrder: FavSortByOptions): void => {
    const updatedFilters = { ...appliedHotFilters, sortBy: sortOrder };
    setAppliedHotFilters?.(updatedFilters);
    handleApplyFilters?.({ triggeredByHandleApply: false });
  };

  const handleSortClick = useCallback(
    (sortedValue: FavSortByOptions) => {
      if (sortByParam !== sortedValue) {
        setSortByParam(sortedValue);
        router.query.sortBy = sortedValue;
        updateParamsInUrl({ sortBy: sortedValue });
        if (totalCount !== null) {
          sendAnalyticEvent('prd_sorting_change', {
            action: sortedValue,
            nb_items: totalCount.toString(),
            sorting: sortedValue,
            filters_applied: '{}',
            filters_details: JSON.stringify(convertAppliedHotFiltersToPayload(appliedHotFilters)),
          });
        }
        handleApplyFiltersWithSort(sortedValue);
      }
    },
    [sortByParam, router, sendAnalyticEvent, totalCount],
  );

  const handleItemsPerPageClick = (count: number): void => {
    setItemsPerPage(count);
    updateParamsInUrl({ pageSize: count.toString() });
    if (count === FAV_CONSTANTS.FAV_DEFAULT_LIMIT) {
      scrollToTop();
    }
    setPaginationQuery({
      limit: count,
      offset: FAV_CONSTANTS.FAV_DEFAULT_OFFSET,
    });
    triggerFocusOnNextUpdate(count === FAV_CONSTANTS.FAV_DEFAULT_LIMIT ? 0 : FAV_CONSTANTS.FAV_DEFAULT_LIMIT);
  };

  const handlePaginationChange = (page: number, itemsPerPage: number): void => {
    const updatedFilters = { ...appliedHotFilters, page: page.toString() };
    setAppliedHotFilters?.(updatedFilters);
    const { limit } = paginationQuery;
    setPaginationQuery({
      limit,
      offset: (page - 1) * itemsPerPage,
    });
    updateParamsInUrl({ page: page.toString() });
    handleApplyFilters?.({ triggeredByHandleApply: false });
    scrollToTop();
  };

  const handleFiltersApply = useCallback(
    (
      newFilters: Record<string, string>,
      filtersWithoutCurrentApplied,
      currentAppliedFilter,
      property = '',
      triggeredByClearFilters = false,
    ) => {
      setAppliedFilters(newFilters);

      if (triggeredByClearFilters) {
        setItemsPerPage(FAV_CONSTANTS.FAV_DEFAULT_LIMIT);
        setPaginationQuery({
          limit: FAV_CONSTANTS.FAV_DEFAULT_LIMIT,
          offset: FAV_CONSTANTS.FAV_DEFAULT_OFFSET,
        });
      }

      setOpenedHotFilterKey(Object.keys(currentAppliedFilter)?.[0]);
      const mergedAppliedFilters = {
        ...currentAppliedFilter,
        universe: newFilters.universe,
      };
      const filtersApplied = Object.values(currentAppliedFilter)?.[0] ? mergedAppliedFilters : {};
      handleApplyFilters?.({
        triggeredByHandleApply: !triggeredByClearFilters, // If triggered by clear filters, don't send apply analytics
        filtersApplied,
        filtersDetails: filtersWithoutCurrentApplied,
        property: Object.keys(currentAppliedFilter)?.[0] || property,
      });
    },
    [totalCount, handleApplyFilters],
  );

  const ProductCardWrapper: FCWithChildren<{ index: number }> = useCallback(
    ({ children, index }) =>
      hasWindowSizeBeenSet && index % rowCount === 0 ? (
        <ImpressionTrackingWrapper
          productsOnRow={products.slice(index, index + rowCount)}
          index={index}
          onRowReveal={onRowReveal}
        >
          {children}
        </ImpressionTrackingWrapper>
      ) : (
        <>{children}</>
      ),
    [onRowReveal, products, rowCount, hasWindowSizeBeenSet],
  );

  const handleProductsLoaded = useCallback(
    ({ isLoaded, products }: { isLoaded: boolean; products: FavoriteProduct[] | null }) => {
      setCmsProductsState({ isLoaded, products });
    },
    [],
  );

  return (
    <>
      <ProductFeedLayout
        customClass={customHeaderClass}
        shouldShowCmsProductsOnEmpty={shouldDisplayCMSBlockInEmptyState && cmsProductsState.isLoaded}
        header={
          shouldDisplayHeader && (
            <FavoritesHeader
              totalCount={totalCount}
              sortBy={sortByParam}
              handleSortClick={handleSortClick}
              isLoadingProducts={isFavoritesDataFetching}
              pageType={pageType}
              hasHeader={hasHeader}
              hasOnlySubheader={hasOnlySubheader}
            />
          )
        }
        hotFilters={
          shouldShowHotFilters && (
            <HotFilters
              appliedFilters={appliedFilters}
              onApplyFilters={handleFiltersApply}
              handleOpenFilter={handleOpenFilter}
              isLoadingProducts={isFavoritesDataFetching}
              pageType={pageType}
            />
          )
        }
        productList={
          <ProductFeed
            isErrorLoadingProducts={isError}
            errorToastComponent={
              <ToastMessage
                key={'error_loading_fav_products'}
                id={`error_fetching_fav_products`}
                title={t('FAVORITE_PAGE.FILTER.RESULTS_ERROR')}
                show={true}
                icon="warning-20"
                type="error"
                duration={5000}
              />
            }
            products={products}
            viewMoreVisible={false}
            pageType={pageType}
            isLoadingMore={isLoadingMoreProducts}
            isLoading={isFavoritesDataFetching}
            emptyState={favouritesEmptyState}
            ProductCardWrapper={ProductCardWrapper}
          />
        }
        pagination={
          products?.length > 0 && (
            <FavoritesPagination
              pageType={pageType}
              paginationMeta={paginationMeta}
              handlePaginationChange={handlePaginationChange}
              handleItemsPerPageClick={handleItemsPerPageClick}
              itemsPerPage={itemsPerPage}
            />
          )
        }
      />
      {/* {shouldDisplayCMSBlockInEmptyState && !!favoritePageData && ( */}
      {shouldDisplayCMSBlockInEmptyState && (
        <div className={styles.favoritesFeedHotFilters__cmsProductsListingBlock}>
          <ProductsProviderListComponent
            onProductsLoaded={handleProductsLoaded}
            {...favoritePageData}
          />
        </div>
      )}
    </>
  );
};

export default FavoritesFeedHotFilters;
